import type { OutgoingIntakeAnswer } from "../../api/types/outgoingIntake";
import type { CaseyProposal } from "./types";

/**
 * Merges an accepted suggestion into an answer.
 *
 * Kept pure and separate from the intake page because this is what the reviewing
 * attorney's trust rests on: the value has to land in the right shape and carry
 * where it came from. A document request accumulates a file; every other
 * question takes a value.
 */
export function answerWithProposal(
  previous: OutgoingIntakeAnswer | undefined,
  proposal: CaseyProposal
): OutgoingIntakeAnswer {
  const base: OutgoingIntakeAnswer = previous ?? {
    question_id: proposal.question_id,
    repeatable_group_id: proposal.repeatable_group_id ?? null,
    repeatable_group_instance_id: proposal.repeatable_group_instance_id ?? null
  };

  const sourceMetadata = {
    origin: proposal.origin,
    document_filename: proposal.document_filename ?? null,
    reference_text: proposal.reference_text ?? null,
    page: proposal.page ?? null,
    confidence: proposal.confidence ?? null
  };

  if (proposal.file) {
    const existing = base.files ?? [];
    // Accepting the same suggestion twice must not double-attach the file.
    if (existing.some((file) => file.file_id === proposal.file?.file_id)) {
      return base;
    }
    return {
      ...base,
      files: [...existing, proposal.file],
      source: "assistant",
      source_metadata: sourceMetadata
    };
  }

  return {
    ...base,
    value: proposal.value,
    source: "assistant",
    source_metadata: sourceMetadata
  };
}

/**
 * One entry of a repeatable group: a single job, address or relative.
 *
 * Structurally the intake page's `GroupInstance`, restated here so this stays a
 * leaf module the page depends on rather than the other way round.
 */
export type GroupEntry = {
  instanceId: string;
  answers: Record<string, OutgoingIntakeAnswer>;
};

/**
 * Merges an accepted suggestion into the repeatable group it belongs to.
 *
 * Opens the entry when Casey names one that does not exist yet, which is the
 * normal case for a resume: it reads three jobs off a document the client
 * uploaded before they ever reached the employment section, and there is nothing
 * to fill. Dropping those answers is what made accepting an employer suggestion
 * look like it did nothing at all.
 */
export function groupsWithProposal(
  groups: Record<string, GroupEntry[]>,
  proposal: CaseyProposal
): Record<string, GroupEntry[]> {
  const groupId = proposal.repeatable_group_id;
  const instanceId = proposal.repeatable_group_instance_id;
  const questionId = proposal.question_id;
  if (!groupId || !instanceId || !questionId) return groups;

  const entries = groups[groupId] ?? [];
  const index = entries.findIndex((entry) => entry.instanceId === instanceId);
  const entry = index < 0 ? { instanceId, answers: {} } : entries[index];

  const filled: GroupEntry = {
    ...entry,
    answers: {
      ...entry.answers,
      [questionId]: answerWithProposal(entry.answers[questionId], proposal)
    }
  };

  const next = [...entries];
  if (index < 0) {
    next.push(filled);
  } else {
    next[index] = filled;
  }
  return { ...groups, [groupId]: next };
}
